import React, { useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { uk } from 'date-fns/locale/uk';
import 'react-datepicker/dist/react-datepicker.css';

import '../styles/VedomList.css';
import { VedomItem } from '../types/app.types';
import { DateUtils } from '../helpers/data_utils';
import { KeyboardUtils } from '../helpers/keyboard_utils';
import { useCalendar } from '../hooks/use_calendar';
import { useModal } from '../hooks/use_modal';
import { useVedomState } from '../hooks/use_vedom_state';
import { VedomService } from '../services/vedom-service';
import { BusMapModal } from './BusMapModal/BusMapModal';

registerLocale('uk', uk);

type TripStatus = 'PLANNED' | 'ACTIVE' | 'FINISHED';

type SortOption = 'time_asc' | 'time_desc';

const STATUS_CONFIG: Record<TripStatus, { label: string; color: string; icon: string }> = {
  ACTIVE: { label: 'Активні', color: '#10b981', icon: '💡' },
  PLANNED: { label: 'Заплановані', color: '#3b82f6', icon: '📅' },
  FINISHED: { label: 'Завершені', color: '#6b7280', icon: '🏁' },
};

const SORT_CONFIG: Record<SortOption, string> = {
  time_asc: 'Час відправлення ↑',
  time_desc: 'Час відправлення ↓'
};

const timeToMinutes = (time?: string): number => {
  if (!time) return Number.MAX_SAFE_INTEGER;
  const [h, m] = time.split(':');
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};

const sortVedomData = (items: VedomItem[], sortBy: SortOption): VedomItem[] => {
  return items.sort((a, b) => {
    switch (sortBy) {
      case 'time_desc':
        return timeToMinutes(b.time) - timeToMinutes(a.time);
      case 'time_asc':
      default:
        return timeToMinutes(a.time) - timeToMinutes(b.time);
    }
  });
};

export const VedomList: React.FC = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const [selectedStatuses, setSelectedStatuses] = useState<TripStatus[]>(['ACTIVE']);
  const [sortBy, setSortBy] = useState<SortOption>('time_asc');

  const calendar = useCalendar();
  const modal = useModal();
  const vedomState = useVedomState();

  const filteredVedomData = useMemo(() => {
    if (!vedomState.vedomData || selectedStatuses.length === 0) {
      return [];
    }

    const filtered = vedomState.vedomData.filter(
      (vedom) => vedom.tripStatus && selectedStatuses.includes(vedom.tripStatus as TripStatus)
    );

    return sortVedomData(filtered, sortBy);
  }, [vedomState.vedomData, selectedStatuses, sortBy]);

  const statusCounts = useMemo(() => {
    if (!vedomState.vedomData) {
      return { PLANNED: 0, ACTIVE: 0, FINISHED: 0 };
    }
    return {
      PLANNED: vedomState.vedomData.filter((v) => v.tripStatus === 'PLANNED').length,
      ACTIVE: vedomState.vedomData.filter((v) => v.tripStatus === 'ACTIVE').length,
      FINISHED: vedomState.vedomData.filter((v) => v.tripStatus === 'FINISHED').length,
    };
  }, [vedomState.vedomData]);

  const handleStatusClick = (status: TripStatus) => {
    setSelectedStatuses((prevStatuses) => {
      if (prevStatuses.includes(status)) {
        return prevStatuses.filter((s) => s !== status);
      } else {
        return [...prevStatuses, status];
      }
    });
  };

  const handleDateChange = (newDate: Date | null) => {
    setDate(newDate);
    if (newDate) {
      calendar.hide();
    }
  };

  const handleFetchVedomList = async () => {
    if (!date) {
      vedomState.setError('Будь ласка, оберіть дату');
      return;
    }
    vedomState.setLoading(true);
    vedomState.clearVedomData();
    setSelectedStatuses(['ACTIVE']);
    try {
      const items = await VedomService.fetchVedomList(date);
      vedomState.setVedomData(items);
    } catch (error) {
      vedomState.setError(error instanceof Error ? error.message : 'Невідома помилка');
    } finally {
      vedomState.setLoading(false);
    }
  };

  const handleOpenBusMapModal = async (vedom: VedomItem) => {
    if (!date) {
      vedomState.setError('Дата не обрана для запиту карти автобуса.');
      return;
    }
    vedomState.setSelectedRoute(vedom);
    modal.open();
    vedomState.setModalLoading(true);
    vedomState.setError(null);
    vedomState.setBusMapData(null);
    try {
      const busMapData = await VedomService.fetchBusMapData(vedom.id, date);
      vedomState.setBusMapData(busMapData);
    } catch (error) {
      vedomState.setError(error instanceof Error ? error.message : 'Невідома помилка');
    } finally {
      vedomState.setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    modal.close();
    vedomState.clearBusMapData();
  };

  const handleRouteKeyPress = (event: React.KeyboardEvent, vedom: VedomItem) => {
    if (KeyboardUtils.isActionKey(event.key)) {
      handleOpenBusMapModal(vedom);
    }
  };

  const getStatusBadgeClass = (status?: string) => {
    if (!status) return 'status-badge status-unknown';
    return `status-badge status-${status.toLowerCase()}`;
  };

  return (
    <div className="vedom-container">
      <div className="vedom-header">
        <h1 className="vedom-title">
          <span className="title-icon">🚌</span>
          Заповненість рейсів
        </h1>
        <p className="vedom-subtitle">
          Оберіть дату та переглядайте доступні рейси з можливістю перегляду карти місць
        </p>
      </div>

      <div className="vedom-card date-selection-card">
        <div className="card-header">
          <h2 className="card-title">
            <span className="card-icon">📅</span>
            Вибір дати рейсу
          </h2>
        </div>
        <div className="vedom-form">
          <div className="date-input-group">
            <label className="form-label">Обрана дата:</label>
            <div className="selected-date-view">
              <p className="selected-date-text">
                {date ? DateUtils.formatForDisplay(date) : 'Дату не обрано'}
              </p>
              <button
                type="button"
                className="change-date-button"
                onClick={calendar.show}
              >
                Змінити
              </button>
            </div>
          </div>
          <button
            type="button"
            className={`fetch-button ${vedomState.loading ? 'loading' : ''}`}
            onClick={handleFetchVedomList}
            disabled={vedomState.loading || !date}
          >
            {vedomState.loading ? (
              <>
                <span className="spinner"></span>
              </>
            ) : (
              <>
                <span className="button-icon">🔍</span>
                <span>Знайти рейси</span>
                </>
            )}
          </button>
        </div>
      </div>

      {calendar.isVisible && (
        <div className="calendar-overlay" onClick={calendar.handleOverlayClick}>
          <div className="calendar-container">
            <button
              type="button"
              className="calendar-close"
              onClick={calendar.hide}
              aria-label="Закрити календар"
            >
              ×
            </button>
            <DatePicker
              selected={date}
              onChange={handleDateChange}
              locale="uk"
              inline
            />
          </div>
        </div>
      )}

      {vedomState.error && (
        <div className="error-card">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <div className="error-text">
              <strong>Помилка:</strong> {vedomState.error}
            </div>
            <button
              type="button"
              onClick={vedomState.clearError}
              className="close-error"
              aria-label="Закрити помилку"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {vedomState.vedomData && vedomState.vedomData.length > 0 && (
        <>
          <div className="vedom-card filters-card">
            <div className="card-header">
              <h3 className="card-title">
                <span className="card-icon">🔍</span>
                Фільтр за статусом
              </h3>
            </div>
            <div className="filters-container">
              {(Object.keys(STATUS_CONFIG) as TripStatus[]).map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`filter-button ${selectedStatuses.includes(status) ? 'active' : ''}`}
                  onClick={() => handleStatusClick(status)}
                  style={{
                    '--filter-color': STATUS_CONFIG[status].color,
                  } as React.CSSProperties}
                >
                  <span className="filter-icon">{STATUS_CONFIG[status].icon}</span>
                  <span className="filter-label">{STATUS_CONFIG[status].label}</span>
                  <span className="filter-count">{statusCounts[status]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="vedom-card routes-card">
            <div className="card-header">
              <h3 className="card-title">
                <span className="card-icon">🚍</span>
                Рейси
                <span>({filteredVedomData.length})</span>
              </h3>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                aria-label="Сортування рейсів"
              >
                {(Object.keys(SORT_CONFIG) as SortOption[]).map((option) => (
                  <option key={option} value={option}>
                    {SORT_CONFIG[option]}
                  </option>
                ))}
              </select>
            </div>
            {selectedStatuses.length > 0 ? (
              filteredVedomData.length > 0 ? (
                <div className="routes-grid">
                  {filteredVedomData.map((vedom) => (
                    <div
                      key={vedom.id}
                      className="route-card"
                      onClick={() => handleOpenBusMapModal(vedom)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => handleRouteKeyPress(e, vedom)}
                    >
                      <div className="route-header">
                        <div className="route-id">{vedom.num || 'N/A'}</div>
                        <div className={getStatusBadgeClass(vedom.tripStatus)}>
                          <span className="status-dot"></span>
                          {vedom.tripStatus === 'ACTIVE' && 'Активний'}
                          {vedom.tripStatus === 'PLANNED' && 'Запланований'}
                          {vedom.tripStatus === 'FINISHED' && 'Завершений'}
                          {!vedom.tripStatus && 'Невідомо'}
                        </div>
                      </div>
                      <div className="route-info">
                        <div className="info-item">
                          <span className="info-icon">🛣️</span>
                          <span className="info-label">Маршрут:</span>
                          <span className="info-value">{vedom.name || 'N/A'}</span>
                        </div>
                        {vedom.busName && (
                          <div className="info-item">
                            <span className="info-icon">🚌</span>
                            <span className="info-label">Автобус:</span>
                            <span className="info-value">{vedom.busName}</span>
                          </div>
                        )}
                        {vedom.time && (
                          <div className="info-item">
                            <span className="info-icon">🕐</span>
                            <span className="info-label">Час:</span>
                            <span className="info-value">{vedom.time}</span>
                          </div>
                        )}
                        {vedom.passengerCount !== undefined && (
                          <div className="info-item">
                            <span className="info-icon">👥</span>
                            <span className="info-label">Пасажири:</span>
                            <span className="info-value">{vedom.passengerCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <span className="no-results-icon">📭</span>
                  <p className="no-results-text">
                    Немає рейсів з обраними статусами
                  </p>
                </div>
              )
            ) : (
              <div className="no-results">
                <span className="no-results-icon">👉</span>
                <p className="no-results-text">
                  Оберіть фільтр, щоб побачити список рейсів
                </p>
              </div>
            )}
          </div>
        </>
      )}

      <BusMapModal
        isOpen={modal.isOpen}
        onClose={handleCloseModal}
        busMapData={vedomState.busMapData}
        loading={vedomState.modalLoading}
        error={vedomState.error}
        selectedRoute={vedomState.selectedRoute}
      />
    </div>
  );
};